import { FC, useEffect, useMemo, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { Container } from "@mui/material";
import { useUserStore } from "../../../stores/user.store";
import { useGetProgramsByIds } from "../../../hooks/useBeneficiaries";
import { Program } from "../../../domain/program.interface";
import EnrollmentSummaryTab from "./tabs/EnrollmentSummaryTab";
import ProgramActivitySummaryTab from "./tabs/ProgramActivitySummaryTab";
import VisitDetailTab from "./tabs/VisitDetailTab";
import AnalyticsAssistantTab from "./tabs/AnalyticsAssistantTab";

interface TabDef {
	id: string;
	label: string;
	/** URL slug — each report is deep-linked at /reports/center/<slug>. */
	slug: string;
	/** Per-tab menu-action code (each tab is a separate, individually granted option). */
	code: string;
}

const TAB_DEFS: TabDef[] = [
	{
		id: "assistant",
		label: "Assistant",
		slug: "assistant",
		code: "REP_DASH_ASSISTANT_VIEW",
	},
	{
		id: "enrollment",
		label: "Enrollment summary",
		slug: "enrollment",
		code: "REP_DASH_ENROLLMENT_VIEW",
	},
	{
		id: "activity",
		label: "Program Summary",
		slug: "program-summary",
		code: "REP_DASH_ACTIVITY_VIEW",
	},
	{
		id: "visit",
		label: "Visit detail",
		slug: "visit-detail",
		code: "REP_DASH_VISIT_VIEW",
	},
];

// Legacy umbrella code: a role that still holds it sees every tab (back-compat
// with the original single Reports Center grant, before per-tab codes existed).
const UMBRELLA_CODE = "REP_DASH_VIEW";

const ReportsDashboard: FC = () => {
	const { userInfo, userHasAccessToCode } = useUserStore();
	// SIMS-7390: the program filter must list only the programs assigned to the
	// logged-in user, not every program in the account.
	const programIds = useMemo(
		() => userInfo?.programs?.map((p) => p.programId) ?? [],
		[userInfo],
	);
	const { data: programs, isLoading: isProgramsLoading } =
		useGetProgramsByIds(programIds);
	const [searchParams] = useSearchParams();
	const { report: reportSlug } = useParams<{ report?: string }>();

	// Filters (incl. program) start EMPTY on every entry to a report link — no
	// selection is carried over between visits. Kept independent per report tab.
	const [programByTab, setProgramByTab] = useState<Record<string, string>>({});

	// Clear, per tab, any program that no longer exists once programs load.
	useEffect(() => {
		if (isProgramsLoading || !programs) return;
		const validIds = new Set(programs.map((p) => p._id));
		setProgramByTab((prev) => {
			let changed = false;
			const next = { ...prev };
			for (const [tab, pid] of Object.entries(prev)) {
				if (pid && !validIds.has(pid)) {
					next[tab] = "";
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [programs, isProgramsLoading]);

	// useGetProgramsByIds returns the beneficiary-domain Program shape; the
	// Reports tabs only read _id/name, so treat it as the program-domain Program
	// (same cast as Beneficiary/ListFilters).
	const programList = useMemo<Program[]>(
		() => (programs ?? []) as unknown as Program[],
		[programs],
	);

	// Each tab is a separately-granted menu option. Show only the tabs the user
	// has a code for; the legacy umbrella code grants all of them.
	const hasUmbrella = userHasAccessToCode(UMBRELLA_CODE);
	const visibleTabs = TAB_DEFS.filter(
		(t) => hasUmbrella || userHasAccessToCode(t.code),
	);

	// Each report has its OWN URL: /reports/center/<slug>. Resolve the active
	// report from the path slug first, then fall back to the legacy ?tab=<id>
	// query param, then the first report the user can see.
	const requestedTab = searchParams.get("tab");
	const activeTabId =
		visibleTabs.find((t) => t.slug === reportSlug)?.id ??
		visibleTabs.find((t) => t.id === requestedTab)?.id ??
		visibleTabs[0]?.id ??
		"";

	// SIMS-7390: a program is always selected — default the active report to the
	// first assigned program when the user hasn't picked one yet.
	useEffect(() => {
		if (isProgramsLoading || !programList.length || !activeTabId) return;
		setProgramByTab((prev) =>
			prev[activeTabId] ? prev : { ...prev, [activeTabId]: programList[0]._id },
		);
	}, [programList, isProgramsLoading, activeTabId]);

	if (visibleTabs.length === 0) {
		return <Navigate to="/unauthorized" replace />;
	}

	// Each report tab gets its OWN program state + change handler.
	const programPropsFor = (
		tabId: string,
	): {
		programId: string;
		programs: Program[];
		onProgramChange: (id: string) => void;
	} => ({
		programId: programByTab[tabId] ?? "",
		programs: programList,
		onProgramChange: (id: string) =>
			setProgramByTab((prev) => ({ ...prev, [tabId]: id })),
	});

	return (
		<Container maxWidth="xl" sx={{ py: 4 }}>
			{/* Each menu option is its own report and deep-links here with
			    ?tab=<id>; render ONLY that report — no tab strip. */}
			{activeTabId === "assistant" && (
				<AnalyticsAssistantTab programs={programList} />
			)}
			{activeTabId === "enrollment" && (
				<EnrollmentSummaryTab {...programPropsFor("enrollment")} />
			)}
			{activeTabId === "activity" && (
				<ProgramActivitySummaryTab {...programPropsFor("activity")} />
			)}
			{activeTabId === "visit" && (
				<VisitDetailTab {...programPropsFor("visit")} />
			)}
		</Container>
	);
};

export default ReportsDashboard;
